import type {
  AdCompleteResponse,
  AdPurpose,
  AdStartResponse,
  BoxesResponse,
  InventoryResponse,
  LeaderboardResponse,
  Look,
  OpenBoxResponse,
  ProfileResponse,
  ShopError,
} from '../../shared/protocol.ts';
import type { LeaderboardPeriod } from '../../shared/periods.ts';

async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`${url}: ${res.status}`);
  return (await res.json()) as T;
}

async function postJson<T>(url: string, body?: unknown): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = (await res.json()) as T & { error?: string };
    return res.ok ? { ok: true, data } : { ok: false, error: data.error ?? 'unavailable' };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export const fetchLeaderboard = (period: LeaderboardPeriod) =>
  getJson<LeaderboardResponse>(`/api/leaderboard?period=${period}`);

/** Null when the player does not exist. */
export async function fetchProfile(username: string): Promise<ProfileResponse | null> {
  const res = await fetch(`/api/users/${encodeURIComponent(username)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`profile: ${res.status}`);
  return (await res.json()) as ProfileResponse;
}

export const fetchInventory = () => getJson<InventoryResponse>('/api/me/inventory');

export const buyItem = (itemId: string) =>
  postJson<InventoryResponse>('/api/shop/buy', { itemId }) as Promise<
    { ok: true; data: InventoryResponse } | { ok: false; error: ShopError | string }
  >;

export const saveLook = (look: Look) => postJson<InventoryResponse>('/api/me/look', look);

export const fetchBoxes = () => getJson<BoxesResponse>('/api/me/boxes');

export const openBox = (id: string) => postJson<OpenBoxResponse>(`/api/me/boxes/${id}/open`);

export const startAd = (purpose: AdPurpose) => postJson<AdStartResponse>('/api/ads/start', { purpose });

export const completeAd = (token: string) => postJson<AdCompleteResponse>('/api/ads/complete', { token });
